(function (global) {
  'use strict';

  function createDefaultRecommendationEngineConfig() {
    return {
      schemaVersion: '1.0.0',
      status: 'pending-approval',
      recommendationVersion: '1.0.0',
      reorderBufferDays: 7,
      recommendationPolicies: [],
      fallbackPolicy: {
        id: 'contact-team-fallback',
        reasonCodes: ['contact-team-required'],
        confidence: 'pending-approval',
        presentation: {
          explanationKeys: [],
          badgeKeys: [],
          highlightKeys: [],
          warningKeys: []
        }
      },
      metadata: {
        owner: 'Nutrition Team',
        status: 'Pending approval',
        lastUpdated: '2026-07-16'
      }
    };
  }

  function isObject(value) {
    return !!value && typeof value === 'object' && !Array.isArray(value);
  }

  function isNonEmptyString(value) {
    return typeof value === 'string' && value.trim() !== '';
  }

  function isFiniteNumber(value) {
    return typeof value === 'number' && Number.isFinite(value);
  }

  function round2(value) {
    return Math.round(value * 100) / 100;
  }

  function getRecommendationEngine(config) {
    if (!config) {
      return null;
    }

    if (config.recommendationEngine) {
      return config.recommendationEngine;
    }

    if (config.schemaVersion && Array.isArray(config.recommendationPolicies)) {
      return config;
    }

    return null;
  }

  function createEmptyObjectiveCalculations(recommendedProductId) {
    return {
      recommendedProductId: recommendedProductId || null,
      recommendedVariantId: null,
      bestValueVariantId: null,
      bagDurationDays: null,
      minimumBagDurationDays: null,
      maximumBagDurationDays: null,
      conservativeBagDurationDays: null,
      reorderAfterDays: null,
      subscriptionSavings: null,
      savingsVsSmallerBag: null,
      savingsVsEquivalentIndividualBags: null,
      savingsVsEquivalentBundle: null,
      recommendationDurationPolicyId: null,
      freeShippingQualified: null
    };
  }

  function createMetadata(policy, recommendationEngine) {
    const reasonCodes = policy && Array.isArray(policy.reasonCodes) ? policy.reasonCodes.slice() : [];
    return {
      recommendationReasonCodes: reasonCodes,
      recommendationPriority: policy && isFiniteNumber(policy.priority) ? policy.priority : 0,
      recommendationConfidence: policy && isNonEmptyString(policy.confidence) ? policy.confidence : 'pending-approval',
      recommendationVersion: recommendationEngine && isNonEmptyString(recommendationEngine.recommendationVersion)
        ? recommendationEngine.recommendationVersion
        : '1.0.0',
      recommendationPolicyId: policy && isNonEmptyString(policy.id) ? policy.id : null
    };
  }

  function normalizeKeyList(values) {
    if (!Array.isArray(values)) {
      return [];
    }

    return values.filter(function (value) {
      return isNonEmptyString(value);
    });
  }

  function createPresentationPayload(policy) {
    const presentation = policy && isObject(policy.presentation) ? policy.presentation : {};

    return {
      explanationKeys: normalizeKeyList(presentation.explanationKeys),
      badgeKeys: normalizeKeyList(presentation.badgeKeys),
      highlightKeys: normalizeKeyList(presentation.highlightKeys),
      warningKeys: normalizeKeyList(presentation.warningKeys)
    };
  }

  function createResponse(status, contactTeamFallback, fallbackReason, objectiveCalculations, metadata, presentationPayload, alternativeProductIds, requiresContactTeamGuidance) {
    return {
      status: status,
      contactTeamFallback: !!contactTeamFallback,
      requiresContactTeamGuidance: !!requiresContactTeamGuidance,
      fallbackReason: fallbackReason || null,
      objectiveCalculations: objectiveCalculations,
      alternativeProductIds: Array.isArray(alternativeProductIds) ? alternativeProductIds.slice() : [],
      recommendationMetadata: metadata,
      presentationPayload: presentationPayload
    };
  }

  function createFallbackRecommendation(input, recommendationEngine, fallbackReason) {
    const fallbackPolicy = recommendationEngine && isObject(recommendationEngine.fallbackPolicy)
      ? recommendationEngine.fallbackPolicy
      : createDefaultRecommendationEngineConfig().fallbackPolicy;
    const productId = input && isNonEmptyString(input.recipeId)
      ? input.recipeId
      : null;

    return createResponse(
      'contact-team',
      true,
      fallbackReason || 'no-matching-recommendation-policy',
      createEmptyObjectiveCalculations(productId),
      createMetadata(fallbackPolicy, recommendationEngine),
      createPresentationPayload(fallbackPolicy)
    );
  }

  function createPlaceholderRecommendation(input) {
    return createResponse(
      'pending-approval',
      false,
      null,
      createEmptyObjectiveCalculations(input && input.recipeId ? input.recipeId : null),
      {
        recommendationReasonCodes: [],
        recommendationPriority: 0,
        recommendationConfidence: 'pending-approval',
        recommendationVersion: '1.0.0',
        recommendationPolicyId: null
      },
      {
        explanationKeys: [],
        badgeKeys: [],
        highlightKeys: [],
        warningKeys: []
      }
    );
  }

  function evaluateCondition(input, condition) {
    if (!condition || !isNonEmptyString(condition.questionId)) {
      return true;
    }

    const actualValue = input && Object.prototype.hasOwnProperty.call(input, condition.questionId)
      ? input[condition.questionId]
      : null;

    switch (condition.operator) {
      case 'equals':
        return actualValue === condition.value;
      case 'notEquals':
        return actualValue !== condition.value;
      case 'in':
        return Array.isArray(condition.value)
          ? condition.value.indexOf(actualValue) !== -1
          : actualValue === condition.value;
      case 'notIn':
        return Array.isArray(condition.value)
          ? condition.value.indexOf(actualValue) === -1
          : actualValue !== condition.value;
      case 'contains':
        return typeof actualValue === 'string' && actualValue.indexOf(condition.value) !== -1;
      case 'exists':
        return actualValue !== null && typeof actualValue !== 'undefined' && actualValue !== '';
      case 'notExists':
        return actualValue === null || typeof actualValue === 'undefined' || actualValue === '';
      case 'greaterThan':
        return isFiniteNumber(actualValue) && isFiniteNumber(condition.value) && actualValue > condition.value;
      case 'greaterThanOrEqual':
        return isFiniteNumber(actualValue) && isFiniteNumber(condition.value) && actualValue >= condition.value;
      case 'lessThan':
        return isFiniteNumber(actualValue) && isFiniteNumber(condition.value) && actualValue < condition.value;
      case 'lessThanOrEqual':
        return isFiniteNumber(actualValue) && isFiniteNumber(condition.value) && actualValue <= condition.value;
      default:
        return false;
    }
  }

  function selectRecommendationPolicy(input, recommendationPolicies) {
    const policies = Array.isArray(recommendationPolicies) ? recommendationPolicies.slice() : [];
    policies.sort(function (left, right) {
      return (right.priority || 0) - (left.priority || 0);
    });

    for (let index = 0; index < policies.length; index += 1) {
      const policy = policies[index];
      const conditions = Array.isArray(policy.conditions) ? policy.conditions : [];
      const matches = conditions.every(function (condition) {
        return evaluateCondition(input, condition);
      });

      if (matches) {
        return policy;
      }
    }

    return null;
  }

  function getProductProfile(recommendationEngine, productId) {
    const eligibility = recommendationEngine && recommendationEngine.recommendationEligibility;
    const profiles = eligibility && Array.isArray(eligibility.productProfiles)
      ? eligibility.productProfiles
      : [];

    return profiles.find(function (profile) {
      return profile && profile.productId === productId;
    }) || null;
  }

  function normalizeAllowlistedInputIds(values, allowedIds) {
    const allowed = new Set(Array.isArray(allowedIds) ? allowedIds : []);
    const seen = new Set();

    return (Array.isArray(values) ? values : []).filter(function (value) {
      if (!isNonEmptyString(value) || !allowed.has(value) || seen.has(value)) {
        return false;
      }
      seen.add(value);
      return true;
    });
  }

  function selectRankedRecommendationCandidates(input, policy, recommendationEngine) {
    const eligibility = recommendationEngine.recommendationEligibility;
    const excludedIngredientIds = normalizeAllowlistedInputIds(
      input && input.excludedIngredientIds,
      eligibility.ingredientExclusionIds
    );
    const requirementIds = normalizeAllowlistedInputIds(
      input && input.requirementIds,
      eligibility.requirementIds
    );
    const preferenceIds = normalizeAllowlistedInputIds(
      input && input.preferenceIds,
      eligibility.preferenceIds
    );
    let candidates = policy.candidateProductIds.map(function (productId) {
      return getProductProfile(recommendationEngine, productId);
    });

    candidates = candidates.filter(function (profile) {
      return profile.availability === 'available';
    });

    candidates = candidates.filter(function (profile) {
      return !excludedIngredientIds.some(function (ingredientId) {
        return profile.ingredientPresence[ingredientId] === true;
      });
    });

    if (preferenceIds.includes('australian-made')) {
      candidates = candidates.filter(function (profile) {
        return profile.australianMade === true;
      });
    }

    if (requirementIds.includes('low-fat')) {
      const promoted = [];
      const remaining = [];
      candidates.forEach(function (profile) {
        if (profile.requirementIds.includes('low-fat')) {
          promoted.push(profile);
        } else {
          remaining.push(profile);
        }
      });
      candidates = promoted.concat(remaining);
    }

    return candidates.map(function (profile) {
      return profile.productId;
    });
  }

  function findVariant(pricingPlan, variantId) {
    if (!pricingPlan || !Array.isArray(pricingPlan.variants) || !variantId) {
      return null;
    }

    return pricingPlan.variants.find(function (variant) {
      return variant && variant.variantId === variantId;
    }) || null;
  }

  function resolveBagDurationDays(pricingPlan, recommendedVariant) {
    if (!recommendedVariant) {
      return null;
    }

    if (pricingPlan && pricingPlan.lifecycleStage === 'puppy') {
      return isFiniteNumber(recommendedVariant.minimumBagDurationDays)
        ? round2(recommendedVariant.minimumBagDurationDays)
        : null;
    }

    return isFiniteNumber(recommendedVariant.bagDurationDays)
      ? round2(recommendedVariant.bagDurationDays)
      : null;
  }

  function resolveReorderAfterDays(bagDurationDays, reorderBufferDays) {
    if (!isFiniteNumber(bagDurationDays)) {
      return null;
    }

    const bufferDays = isFiniteNumber(reorderBufferDays) && reorderBufferDays >= 0
      ? reorderBufferDays
      : 0;

    return Math.max(0, Math.floor(bagDurationDays - bufferDays));
  }

  function resolveObjectiveCalculations(policy, pricingPlan, reorderBufferDays, input, recommendationEngine, selectedProductId) {
    const recommendedProductId = isNonEmptyString(selectedProductId)
      ? selectedProductId
      : (policy && isNonEmptyString(policy.productId)
        ? policy.productId
        : (pricingPlan && pricingPlan.recipeId) || (input && input.recipeId) || null);
    const objectiveCalculations = createEmptyObjectiveCalculations(recommendedProductId);

    if (!pricingPlan || pricingPlan.status !== 'calculated') {
      return objectiveCalculations;
    }

    objectiveCalculations.recommendedVariantId = pricingPlan.recommendedBag || null;
    objectiveCalculations.bestValueVariantId = pricingPlan.bestValueBag || null;

    const recommendedVariant = findVariant(pricingPlan, objectiveCalculations.recommendedVariantId)
      || findVariant(pricingPlan, objectiveCalculations.bestValueVariantId);

    if (!recommendedVariant) {
      return objectiveCalculations;
    }

    const isPuppy = pricingPlan.lifecycleStage === 'puppy';

    objectiveCalculations.bagDurationDays = resolveBagDurationDays(pricingPlan, recommendedVariant);
    objectiveCalculations.reorderAfterDays = resolveReorderAfterDays(objectiveCalculations.bagDurationDays, reorderBufferDays);
    objectiveCalculations.subscriptionSavings = isFiniteNumber(recommendedVariant.subscriptionSavings)
      ? recommendedVariant.subscriptionSavings
      : null;
    objectiveCalculations.savingsVsSmallerBag = isFiniteNumber(recommendedVariant.savingsVsSmallestBag)
      ? recommendedVariant.savingsVsSmallestBag
      : null;
    objectiveCalculations.savingsVsEquivalentIndividualBags = isFiniteNumber(recommendedVariant.savingsVsEquivalentIndividualBags)
      ? recommendedVariant.savingsVsEquivalentIndividualBags
      : null;
    objectiveCalculations.savingsVsEquivalentBundle = isFiniteNumber(recommendedVariant.savingsVsEquivalentBundle)
      ? recommendedVariant.savingsVsEquivalentBundle
      : null;
    objectiveCalculations.freeShippingQualified = typeof recommendedVariant.qualifiesForFreeShipping === 'boolean'
      ? recommendedVariant.qualifiesForFreeShipping
      : null;

    if (isPuppy) {
      objectiveCalculations.minimumBagDurationDays = isFiniteNumber(recommendedVariant.minimumBagDurationDays)
        ? round2(recommendedVariant.minimumBagDurationDays)
        : null;
      objectiveCalculations.maximumBagDurationDays = isFiniteNumber(recommendedVariant.maximumBagDurationDays)
        ? round2(recommendedVariant.maximumBagDurationDays)
        : null;
      objectiveCalculations.conservativeBagDurationDays = objectiveCalculations.minimumBagDurationDays;
      objectiveCalculations.recommendationDurationPolicyId = recommendationEngine && isNonEmptyString(recommendationEngine.puppyDurationPolicyId)
        ? recommendationEngine.puppyDurationPolicyId
        : null;
    } else {
      objectiveCalculations.conservativeBagDurationDays = objectiveCalculations.bagDurationDays;
    }

    return objectiveCalculations;
  }

  function validatePresentation(presentation, path, errors) {
    const requiredKeys = ['explanationKeys', 'badgeKeys', 'highlightKeys', 'warningKeys'];

    if (!isObject(presentation)) {
      errors.push(path + ' must be an object.');
      return;
    }

    requiredKeys.forEach(function (key) {
      if (!Array.isArray(presentation[key])) {
        errors.push(path + '.' + key + ' must be an array.');
        return;
      }

      presentation[key].forEach(function (value) {
        if (!isNonEmptyString(value)) {
          errors.push(path + '.' + key + ' entries must be non-empty strings.');
        }
      });
    });
  }

  function validateConditions(conditions, path, errors) {
    const supportedOperators = {
      equals: true,
      notEquals: true,
      in: true,
      notIn: true,
      contains: true,
      exists: true,
      notExists: true,
      greaterThan: true,
      greaterThanOrEqual: true,
      lessThan: true,
      lessThanOrEqual: true
    };

    if (!Array.isArray(conditions)) {
      errors.push(path + ' must be an array.');
      return;
    }

    conditions.forEach(function (condition, index) {
      const conditionPath = path + '[' + index + ']';

      if (!isObject(condition)) {
        errors.push(conditionPath + ' must be an object.');
        return;
      }

      if (!isNonEmptyString(condition.questionId)) {
        errors.push(conditionPath + '.questionId is required.');
      }

      if (!supportedOperators[condition.operator]) {
        errors.push(conditionPath + '.operator is unsupported.');
      }

      if (condition.operator !== 'exists' && condition.operator !== 'notExists' && typeof condition.value === 'undefined') {
        errors.push(conditionPath + '.value is required.');
      }
    });
  }

  function validateUniqueStringList(values, path, errors, allowEmpty) {
    const seen = Object.create(null);

    if (!Array.isArray(values) || (!allowEmpty && !values.length)) {
      errors.push(path + ' must be ' + (allowEmpty ? 'an array.' : 'a non-empty array.'));
      return [];
    }

    values.forEach(function (value) {
      if (!isNonEmptyString(value)) {
        errors.push(path + ' entries must be non-empty strings.');
      } else if (seen[value]) {
        errors.push(path + ' must not contain duplicates.');
      } else {
        seen[value] = true;
      }
    });

    return Object.keys(seen);
  }

  function validateRecommendationEligibility(recommendationEngine, errors) {
    const path = 'recommendationEngine.recommendationEligibility';
    const eligibility = recommendationEngine.recommendationEligibility;
    const availabilityValues = ['available', 'temporarily-unavailable'];

    if (!isObject(eligibility)) {
      errors.push(path + ' is required.');
      return { productIds: [] };
    }

    const ingredientIds = validateUniqueStringList(
      eligibility.ingredientExclusionIds,
      path + '.ingredientExclusionIds',
      errors,
      false
    );
    const requirementIds = validateUniqueStringList(
      eligibility.requirementIds,
      path + '.requirementIds',
      errors,
      true
    );
    validateUniqueStringList(
      eligibility.preferenceIds,
      path + '.preferenceIds',
      errors,
      true
    );

    if (!Array.isArray(eligibility.productProfiles) || !eligibility.productProfiles.length) {
      errors.push(path + '.productProfiles must be a non-empty array.');
      return { productIds: [] };
    }

    const productIds = [];
    const seenProductIds = Object.create(null);
    eligibility.productProfiles.forEach(function (profile, index) {
      const profilePath = path + '.productProfiles[' + index + ']';
      if (!isObject(profile)) {
        errors.push(profilePath + ' must be an object.');
        return;
      }

      if (!isNonEmptyString(profile.productId)) {
        errors.push(profilePath + '.productId is required.');
      } else if (seenProductIds[profile.productId]) {
        errors.push(path + '.productProfiles must not contain duplicate productId values.');
      } else {
        seenProductIds[profile.productId] = true;
        productIds.push(profile.productId);
      }

      if (!availabilityValues.includes(profile.availability)) {
        errors.push(profilePath + '.availability must be available or temporarily-unavailable.');
      }

      if (!isObject(profile.ingredientPresence)) {
        errors.push(profilePath + '.ingredientPresence must be an object.');
      } else {
        const profileIngredientKeys = Object.keys(profile.ingredientPresence);
        ingredientIds.forEach(function (ingredientId) {
          if (typeof profile.ingredientPresence[ingredientId] !== 'boolean') {
            errors.push(profilePath + '.ingredientPresence.' + ingredientId + ' must be a boolean.');
          }
        });
        profileIngredientKeys.forEach(function (ingredientId) {
          if (!ingredientIds.includes(ingredientId)) {
            errors.push(profilePath + '.ingredientPresence contains an unsupported ingredient id.');
          }
        });
      }

      const profileRequirementIds = validateUniqueStringList(
        profile.requirementIds,
        profilePath + '.requirementIds',
        errors,
        true
      );
      profileRequirementIds.forEach(function (requirementId) {
        if (!requirementIds.includes(requirementId)) {
          errors.push(profilePath + '.requirementIds contains an unsupported requirement id.');
        }
      });

      if (typeof profile.australianMade !== 'boolean') {
        errors.push(profilePath + '.australianMade must be a boolean.');
      }
    });

    return { productIds: productIds };
  }

  function validateRecommendationEngineConfig(config) {
    const errors = [];
    const recommendationEngine = getRecommendationEngine(config);
    const seenPolicyIds = Object.create(null);

    if (!recommendationEngine) {
      return { isValid: false, errors: ['recommendationEngine configuration is missing.'] };
    }

    const isEligibilityVersion = recommendationEngine.schemaVersion === '1.1.0';
    if (recommendationEngine.schemaVersion !== '1.0.0' && !isEligibilityVersion) {
      errors.push('recommendationEngine.schemaVersion must be 1.0.0 or 1.1.0.');
    }

    if (!isNonEmptyString(recommendationEngine.status)) {
      errors.push('recommendationEngine.status is required.');
    }

    if (!isNonEmptyString(recommendationEngine.recommendationVersion)) {
      errors.push('recommendationEngine.recommendationVersion is required.');
    }

    if (!isFiniteNumber(recommendationEngine.reorderBufferDays) || recommendationEngine.reorderBufferDays < 0) {
      errors.push('recommendationEngine.reorderBufferDays must be a non-negative number.');
    }

    const eligibilityValidation = isEligibilityVersion
      ? validateRecommendationEligibility(recommendationEngine, errors)
      : { productIds: [] };

    if (!Array.isArray(recommendationEngine.recommendationPolicies)) {
      errors.push('recommendationEngine.recommendationPolicies must be an array.');
    } else {
      recommendationEngine.recommendationPolicies.forEach(function (policy, index) {
        const path = 'recommendationEngine.recommendationPolicies[' + index + ']';

        if (!isObject(policy)) {
          errors.push(path + ' must be an object.');
          return;
        }

        if (!isNonEmptyString(policy.id)) {
          errors.push(path + '.id is required.');
        } else if (seenPolicyIds[policy.id]) {
          errors.push('recommendationEngine.recommendationPolicies contains duplicate id values.');
        } else {
          seenPolicyIds[policy.id] = true;
        }

        if (!isFiniteNumber(policy.priority)) {
          errors.push(path + '.priority must be a number.');
        }

        if (!isNonEmptyString(policy.productId)) {
          errors.push(path + '.productId is required.');
        }

        if (isEligibilityVersion) {
          const candidateProductIds = validateUniqueStringList(
            policy.candidateProductIds,
            path + '.candidateProductIds',
            errors,
            false
          );
          if (candidateProductIds.length && policy.productId !== candidateProductIds[0]) {
            errors.push(path + '.productId must match the first candidateProductIds entry.');
          }
          candidateProductIds.forEach(function (productId) {
            if (!eligibilityValidation.productIds.includes(productId)) {
              errors.push(path + '.candidateProductIds contains an unknown product id.');
            }
          });
        }

        if (!Array.isArray(policy.reasonCodes) || !policy.reasonCodes.length) {
          errors.push(path + '.reasonCodes must be a non-empty array.');
        } else {
          policy.reasonCodes.forEach(function (reasonCode) {
            if (!isNonEmptyString(reasonCode)) {
              errors.push(path + '.reasonCodes entries must be non-empty strings.');
            }
          });
        }

        if (!isNonEmptyString(policy.confidence)) {
          errors.push(path + '.confidence is required.');
        }

        validateConditions(policy.conditions, path + '.conditions', errors);
        validatePresentation(policy.presentation, path + '.presentation', errors);
      });
    }

    if (!isObject(recommendationEngine.fallbackPolicy)) {
      errors.push('recommendationEngine.fallbackPolicy is required.');
    } else {
      if (!isNonEmptyString(recommendationEngine.fallbackPolicy.id)) {
        errors.push('recommendationEngine.fallbackPolicy.id is required.');
      }

      if (!Array.isArray(recommendationEngine.fallbackPolicy.reasonCodes) || !recommendationEngine.fallbackPolicy.reasonCodes.length) {
        errors.push('recommendationEngine.fallbackPolicy.reasonCodes must be a non-empty array.');
      }

      if (!isNonEmptyString(recommendationEngine.fallbackPolicy.confidence)) {
        errors.push('recommendationEngine.fallbackPolicy.confidence is required.');
      }

      validatePresentation(recommendationEngine.fallbackPolicy.presentation, 'recommendationEngine.fallbackPolicy.presentation', errors);
    }

    return {
      isValid: errors.length === 0,
      errors: errors
    };
  }

  function buildRecommendation(input, config, pricingPlan) {
    const recommendationEngine = getRecommendationEngine(config || {});
    const validation = validateRecommendationEngineConfig(config || {});

    if (!validation.isValid) {
      return createFallbackRecommendation(input, recommendationEngine, 'invalid-recommendation-engine-configuration');
    }

    if (!recommendationEngine || !Array.isArray(recommendationEngine.recommendationPolicies) || !recommendationEngine.recommendationPolicies.length) {
      return createFallbackRecommendation(input, recommendationEngine, 'recommendation-policies-not-available');
    }

    const policy = selectRecommendationPolicy(input || {}, recommendationEngine.recommendationPolicies);

    if (!policy) {
      return createFallbackRecommendation(input, recommendationEngine, 'no-matching-recommendation-policy');
    }

    const rankedProductIds = recommendationEngine.schemaVersion === '1.1.0'
      ? selectRankedRecommendationCandidates(input || {}, policy, recommendationEngine)
      : [policy.productId];

    if (!rankedProductIds.length) {
      return createFallbackRecommendation(input, recommendationEngine, 'no-eligible-recommendation-candidate');
    }

    const selectedProductId = rankedProductIds[0];

    return createResponse(
      'recommended',
      false,
      null,
      resolveObjectiveCalculations(policy, pricingPlan, recommendationEngine.reorderBufferDays, input, recommendationEngine, selectedProductId),
      createMetadata(policy, recommendationEngine),
      createPresentationPayload(policy),
      rankedProductIds.slice(1),
      false
    );
  }

  const api = {
    createDefaultRecommendationEngineConfig,
    validateRecommendationEngineConfig,
    createPlaceholderRecommendation,
    buildRecommendation
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }

  global.RecommendationEngine = api;
})(typeof globalThis !== 'undefined' ? globalThis : this);
